/**
 * useDdlStatementDetection — owns the cursor → DDL-statement → pixel-position
 * pipeline, consumed by the Compact DDL editor's edit-context wiring
 * (`compact/useCompactDdlEditContext`). Mirrors VQE-Edit's
 * `useInlineToolbarPosition` but classifies DDL specifically; mutual
 * exclusivity with the InlineVqeToolbar is enforced by the host
 * (`QueryEditorTab`) which gates the VQE toolbar's `enabled` flag on
 * `state.visible === false`.
 *
 * Resolver-classification only — no regex (per VQE-D10). The buffer is parsed
 * once via `buildParsedDocumentFromSource` and the DDL kind is decided from
 * `classifyDdlAtCursor`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { buildParsedDocumentFromSource, getDocumentStructure } from '../../../sql-grammar/document';
import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import { lineColumnToOffset } from '../../visual-query-editor/inline/useInlineToolbarPosition';
import { classifyDdlAtCursor } from './classifyDdlAtCursor';
import type { DdlClassification } from './classifyDdlAtCursor';

const DEBOUNCE_MS = 150;

export interface DdlToolbarAnchor {
  readonly visible: true;
  readonly top: number;
  readonly left: number;
  readonly statement: StatementInfo;
  readonly classification: DdlClassification;
  readonly cursorOffset: number;
  readonly bufferText: string;
}

export type DdlToolbarState =
  | { readonly visible: false }
  | DdlToolbarAnchor;

const HIDDEN: DdlToolbarState = { visible: false };

export interface UseDdlStatementDetectionArgs {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly query: string;
  readonly options?: ParseOptions;
  /** When false the hook short-circuits and stays hidden. */
  readonly enabled?: boolean;
}

const DEFAULT_OPTIONS: ParseOptions = { engine: 'postgres' };

export interface ClassifyDdlArgs {
  readonly query: string;
  readonly cursorOffset: number;
  readonly options: ParseOptions;
}

export type ClassifyDdlResult = {
  readonly statement: StatementInfo;
  readonly classification: DdlClassification;
} | null;

/**
 * Pure helper: parse the buffer, find the statement under the cursor,
 * classify it. Returns `null` when the cursor is outside any statement or
 * the statement is not a recognised DDL form.
 */
export function classifyDdlForCursor({ query, cursorOffset, options }: ClassifyDdlArgs): ClassifyDdlResult {
  if (query.trim().length === 0) return null;
  const parsedDocument = buildParsedDocumentFromSource(query, options);
  const documentStructure = getDocumentStructure(parsedDocument, 'inline-ddl-toolbar');
  const statement = documentStructure.statementAt(cursorOffset) ?? documentStructure.statements[0];
  if (!statement) return null;
  const classification = classifyDdlAtCursor(query, options, statement);
  if (!classification) return null;
  return { statement, classification };
}

export function useDdlStatementDetection({
  editorRef,
  query,
  options = DEFAULT_OPTIONS,
  enabled = true,
}: UseDdlStatementDetectionArgs): DdlToolbarState {
  const [state, setState] = useState<DdlToolbarState>(HIDDEN);
  const queryRef = useRef(query);
  const optionsRef = useRef(options);
  const enabledRef = useRef(enabled);

  useEffect(() => { queryRef.current = query; }, [query]);
  useEffect(() => { optionsRef.current = options; }, [options]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const recompute = useCallback(() => {
    if (!enabledRef.current) {
      setState(HIDDEN);
      return;
    }
    const editor = editorRef.current;
    if (!editor) {
      setState(HIDDEN);
      return;
    }
    const cursor = editor.getCursorPosition();
    if (!cursor) {
      setState(HIDDEN);
      return;
    }
    const currentQuery = queryRef.current;
    const cursorOffset = lineColumnToOffset(currentQuery, cursor.lineNumber, cursor.column);
    const classified = classifyDdlForCursor({
      query: currentQuery,
      cursorOffset,
      options: optionsRef.current,
    });
    if (!classified) {
      setState(HIDDEN);
      return;
    }
    const { statement, classification } = classified;
    const anchorLine = statement.range.startLine + 1;
    const pixel = editor.getPixelPositionForPosition(anchorLine, 1);
    if (!pixel) {
      setState(HIDDEN);
      return;
    }
    setState({
      visible: true,
      top: Math.max(0, pixel.top),
      left: Math.max(0, pixel.left),
      statement,
      classification,
      cursorOffset,
      bufferText: currentQuery,
    });
  }, [editorRef]);

  const debouncedRecompute = useDebouncedCallback(recompute, DEBOUNCE_MS);

  useEffect(() => {
    if (!enabled) {
      setState(HIDDEN);
      return undefined;
    }
    let cancelled = false;
    let unsubscribeCursor: (() => void) | null = null;
    let unsubscribeScroll: (() => void) | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const tryAttach = () => {
      if (cancelled) return;
      const editor = editorRef.current;
      if (!editor) {
        pollTimer = setTimeout(tryAttach, 100);
        return;
      }
      const cursor = editor.getCursorPosition();
      if (!cursor) {
        recompute();
        pollTimer = setTimeout(tryAttach, 100);
        return;
      }
      recompute();
      unsubscribeCursor = editor.onDidChangeCursorPosition(() => debouncedRecompute());
      unsubscribeScroll = editor.onDidScrollChange(() => recompute());
    };
    tryAttach();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      unsubscribeCursor?.();
      unsubscribeScroll?.();
    };
  }, [editorRef, recompute, debouncedRecompute, enabled, query]);

  return state;
}
